import { useState, useEffect, useReducer, useRef } from 'react';
import Paywall from './Paywall';
import Payment from './Payment';
import Confirmation from './Confirmation';
import Billing from '../Settings/Billing';
import LogOutButton from 'Components/LogOutButton';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { MainAppReducer } from 'Types';
import { isActiveSubscription } from 'Utils/Subscription';
import { onEntitlementRequired } from 'Utils/entitlementGate';
import { onPaywallRequested } from 'Utils/paywallRequest';
import { isCapacitorNative } from 'Utils/Constants';
import { canCheckForNewerBuild, reloadIfNewerBuild } from 'Utils/currentBuild';
import { storeProviderOf } from 'Billing/StoreSubscription';
import axios from 'axios';
import { Box, Button } from '@mui/material';
import { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';
// import { useAuth } from "react-oidc-context";

/**
 * The paywall a store app opens by choice (MUS-605): whether it is asked for, and how many times
 * the account changed under one that was. That count keys the paywall, so a paywall the account
 * changed under is replaced by a fresh one, while a paywall nobody opened by choice is never
 * remounted on its account.
 */
interface ChosenPaywall {
  requested: boolean;
  timesTheAccountChangedUnderIt: number;
}
type ChosenPaywallEvent = 'requested' | 'closed' | 'account-changed';

/** Statuses whose subscription is resolved, not bought again, while one exists to resolve. */
const RECOVERY_STATUSES = ['unpaid', 'incomplete', 'paused'];
/** How long a paywall waits for the current-build check before it shows anyway (MUS-630). */
const BUILD_CHECK_WAIT_MS = 1500;

interface BillingDetailsAnswer {
  isCustomer?: boolean;
  hasPaymentMethodId?: boolean;
}

/**
 * Billing says outright that the account has no Stripe customer (none, or one deleted at Stripe):
 * nothing to resolve. Anything else keeps the dialog. In particular "no recoverable subscription"
 * does not: it is worked out from the newest subscription only, while checkout refuses on any
 * older one that can still bill, so trusting it could leave the paywall with no way out.
 */
const hasNoSubscriptionToResolve = (details?: BillingDetailsAnswer) =>
  details?.isCustomer === false;

function chosenPaywallAfter(paywall: ChosenPaywall, event: ChosenPaywallEvent): ChosenPaywall {
  if (event === 'requested') return paywall.requested ? paywall : { ...paywall, requested: true };
  if (!paywall.requested) return paywall;
  return event === 'account-changed'
    ? { requested: false, timesTheAccountChangedUnderIt: paywall.timesTheAccountChangedUnderIt + 1 }
    : { ...paywall, requested: false };
}

const PaymentFlow = () => {
  // const data = useSelector((state: MainAppReducer) => state.mainAppReducer)
  // const { user } = useAuth();
  const { user: _user, currentAuthToken } = useAuthWrapperContext();

  const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/`;
  const { userData } = useSelector((state: MainAppReducer) => state.mainAppReducer);

  const [showPaywall, setShowPaywall] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [planTypeSelected, setPlanTypeSelected] = useState('');
  const [discountCodeValid, setDiscountCodeValid] = useState<boolean | null>(null);
  const [discountCode, setDiscountCode] = useState('');
  const [showDiscountCode, setShowDiscountCode] = useState(false);
  const [hasPaymentMethod, setHasPaymentMethod] = useState<boolean | null>(null);
  const [billingError, setBillingError] = useState(false);
  const [billingRevision, setBillingRevision] = useState(0);
  const [nothingToResolve, setNothingToResolve] = useState(false);
  const [buildCheckable] = useState(canCheckForNewerBuild);
  const [paywallBuildChecked, setPaywallBuildChecked] = useState(false);
  const [chosenPaywall, tellChosenPaywall] = useReducer(chosenPaywallAfter, {
    requested: false,
    timesTheAccountChangedUnderIt: 0,
  });
  const wasPaymentGateShowing = useRef(false);
  const native = isCapacitorNative();

  const _location = useLocation();
  // If paywall; not allowed to exit.
  const paywallExitAllowed = false; //location.pathname !== '/lesson'

  useEffect(() => {
    let canceled = false;
    setBillingError(false);
    setHasPaymentMethod(null);
    setNothingToResolve(false);
    if (
      userData?.subscription_status === 'past_due' &&
      ['apple', 'google'].includes(userData.subscription_provider || '')
    ) {
      setHasPaymentMethod(true);
    } else if (
      userData?.subscription_status === 'past_due' ||
      (RECOVERY_STATUSES.includes(userData?.subscription_status || '') &&
        !storeProviderOf(userData?.subscription_provider))
    ) {
      (async () => {
        try {
          const token = await currentAuthToken();
          const { data } = await axios.get<{ details?: BillingDetailsAnswer }>(
            `${baseUrl}stripe/user-billing-details`,
            {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 15000,
            },
          );
          if (canceled) return;
          setHasPaymentMethod(!!data.details?.isCustomer && !!data.details?.hasPaymentMethodId);
          setNothingToResolve(hasNoSubscriptionToResolve(data.details));
        } catch {
          // An unavailable billing service is not evidence that it is safe to buy again.
          if (!canceled) setBillingError(true);
        }
      })();
    }
    return () => {
      canceled = true;
    };
  }, [
    userData?.subscription_status,
    userData?.subscription_provider,
    userData?.user_id,
    currentAuthToken,
    baseUrl,
    billingRevision,
  ]);

  useEffect(() => {
    if (userData?.coupon_code) {
      // If they have coupon code in userData; that means that the code was valid.
      // WE will need to ensure they are grandfathered in if we decide to rotate coupon codes (possibly query and update all users with a certain code and update them so that when the trial expires they will have discount!)
      setDiscountCode(userData?.coupon_code);
      setShowDiscountCode(true);
      setDiscountCodeValid(true);
    }
    const subscriptionStatus = userData?.subscription_status;
    if (showPaywall || showPayment) {
      wasPaymentGateShowing.current = true;
    }

    if (!subscriptionStatus) {
      setShowPaywall(false);
      setShowPayment(false);
      wasPaymentGateShowing.current = false;
      return;
    }

    if (subscriptionStatus === 'past_due' && hasPaymentMethod == null) {
      return;
    }
    // Until billing answers, it is not known whether there is a subscription to resolve.
    if (
      RECOVERY_STATUSES.includes(subscriptionStatus) &&
      !storeProviderOf(userData?.subscription_provider) &&
      hasPaymentMethod == null &&
      !billingError
    ) {
      return;
    }

    if (isActiveSubscription(subscriptionStatus, hasPaymentMethod ?? false)) {
      const shouldShowConfirmation = wasPaymentGateShowing.current;
      setShowPaywall(false);
      setShowPayment(false);
      if (shouldShowConfirmation) {
        setShowConfirmation(true);
        wasPaymentGateShowing.current = false;
      }
      return;
    }

    // With no subscription left to resolve, resolving is a dead end: the paywall shows instead.
    if (
      !nothingToResolve &&
      (RECOVERY_STATUSES.includes(subscriptionStatus) ||
        (subscriptionStatus === 'past_due' && hasPaymentMethod === false))
    ) {
      setShowPaywall(false);
      setShowPayment(false);
      return;
    }

    if (!showConfirmation) {
      setShowPayment(false);
      setShowPaywall(true);
    }
  }, [
    userData,
    hasPaymentMethod,
    billingError,
    nothingToResolve,
    showPaywall,
    showPayment,
    showConfirmation,
  ]);

  const waitingForRecoveryBilling =
    RECOVERY_STATUSES.includes(userData?.subscription_status || '') &&
    !storeProviderOf(userData?.subscription_provider) &&
    hasPaymentMethod === null &&
    !billingError;
  const needsRecovery =
    !nothingToResolve &&
    !waitingForRecoveryBilling &&
    (RECOVERY_STATUSES.includes(userData?.subscription_status || '') ||
      (userData?.subscription_status === 'past_due' && hasPaymentMethod === false));
  // MUS-532: when the server's Layer-2 entitlement gate denies a content fetch
  // (403), the shared axios layer fires this signal. Force the paywall so a
  // load race (fetch beats the paywall render) shows the paywall rather than an
  // error/blank/crash. The subscription-status reconciliation effect above still
  // governs the entitled case — an entitled-looking user is not trapped here
  // (that client/server split-brain is MUS-533's product decision), so this only
  // makes the paywall appear promptly for a genuinely-lapsed user.
  useEffect(() => {
    const unsubscribe = onEntitlementRequired(() => {
      setShowPaywall(true);
    });
    return unsubscribe;
  }, []);

  // MUS-605: in the store apps the Billing tab sells nothing itself. It asks for
  // this paywall instead, which the user may close again. Web never asks.
  useEffect(
    () =>
      onPaywallRequested(() => {
        if (isCapacitorNative()) tellChosenPaywall('requested');
      }),
    [],
  );
  // A paywall opened by choice belongs to the account state it was opened in:
  // a purchase, a restore or another account signing in closes it.
  useEffect(() => {
    tellChosenPaywall('account-changed');
  }, [userData?.user_id, userData?.subscription_status, userData?.subscription_provider]);

  const paywallRequired =
    showPaywall &&
    !needsRecovery &&
    !waitingForRecoveryBilling &&
    !(userData?.subscription_status === 'past_due' && hasPaymentMethod === null);

  // MUS-630: before a web paywall is shown, make sure this tab runs the build the site serves now.
  // It waits briefly for the answer, so a tab about to reload does not show its old paywall first,
  // then shows the paywall anyway; after that it no longer reloads.
  useEffect(() => {
    if (!paywallRequired || !buildCheckable) {
      setPaywallBuildChecked(false);
      return;
    }
    let canceled = false;
    // Once the paywall is in front of the person a late answer never reloads it: a reload could
    // cut off a purchase or the move to checkout.
    let shown = false;
    const showPaywall = () => {
      shown = true;
      setPaywallBuildChecked(true);
    };
    const showAnyway = setTimeout(showPaywall, BUILD_CHECK_WAIT_MS);
    reloadIfNewerBuild({ mayReload: () => !canceled && !shown }).then((reloading) => {
      if (canceled || reloading) return;
      clearTimeout(showAnyway);
      showPaywall();
    });
    return () => {
      canceled = true;
      clearTimeout(showAnyway);
    };
  }, [paywallRequired, buildCheckable]);
  const paywallReady = paywallRequired && (paywallBuildChecked || !buildCheckable);
  const paywallOpenedByChoice =
    chosenPaywall.requested && !paywallRequired && !needsRecovery && !billingError;
  const closePaywallOpenedByChoice = () => tellChosenPaywall('closed');

  return (
    <>
      {billingError && userData?.subscription_status === 'past_due' && (
        <>
          <Box className="modal-outer" />
          <Box className="modal-inner2" role="alert">
            <p className="paywall-desc-text">
              We couldn’t confirm your existing subscription. Please retry before making another
              purchase.
            </p>
            <Button onClick={() => setBillingRevision((value) => value + 1)}>Retry billing</Button>
            <LogOutButton />
          </Box>
        </>
      )}
      {needsRecovery && (
        <>
          <Box className="modal-outer" />
          <Box
            className="modal-inner2"
            role="dialog"
            aria-modal="true"
            aria-label="Resolve your subscription"
            sx={{ justifyContent: 'flex-start', overflowY: 'auto', gap: 1 }}
          >
            <h3 className="paywall-desc-title">Resolve your current subscription</h3>
            <p className="paywall-desc-text">
              {native
                ? 'Your subscription needs attention before you can continue. For help, email support@museflow.ai.'
                : 'Review your current billing terms and update your payment details to continue.'}
            </p>
            <Billing canOpenPaywall={false} />
            <LogOutButton />
          </Box>
        </>
      )}
      {(paywallReady || paywallOpenedByChoice) && (
        <Paywall
          // When the account changes under a paywall opened by choice (the trial ends while it
          // is open), the paywall that follows starts again, so nothing worked out for the
          // earlier state is kept. Nothing else changes this key: a paywall the user must pass
          // is never restarted by its account, nor an open one by a server refusal.
          key={chosenPaywall.timesTheAccountChangedUnderIt}
          setShowPaywall={paywallRequired ? setShowPaywall : closePaywallOpenedByChoice}
          setShowPayment={setShowPayment}
          setPlanTypeSelected={setPlanTypeSelected}
          exitAllowed={paywallRequired ? paywallExitAllowed : true}
          discountCodeValid={discountCodeValid}
          setDiscountCodeValid={setDiscountCodeValid}
          discountCode={discountCode}
          setDiscountCode={setDiscountCode}
          showDiscountCode={showDiscountCode}
          setShowDiscountCode={setShowDiscountCode}
        />
      )}
      {showPayment && (
        <Payment
          setShowPaywall={setShowPaywall}
          setShowPayment={setShowPayment}
          setShowConfirmation={setShowConfirmation}
          planTypeSelected={planTypeSelected}
          exitAllowed={false}
          discountCode={discountCode}
          discountCodeValid={discountCodeValid}
          setDiscountCodeValid={setDiscountCodeValid}
        />
      )}
      {showConfirmation && <Confirmation setShowConfirmation={setShowConfirmation} />}
    </>
  );
};
export default PaymentFlow;
